'use client';

import React, { useState, useCallback } from 'react';

import { Box, BoxProps } from './box';

export interface SliderProps extends Omit<BoxProps, 'onChange'> {
  value?: number;
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  isDisabled?: boolean;
  onChange?: (value: number) => void;
  onChangeStart?: (value: number) => void;
  onChangeEnd?: (value: number) => void;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export type SliderTrackProps = BoxProps;

export type SliderThumbProps = BoxProps;

export const Slider = React.forwardRef<HTMLDivElement, SliderProps>(
  (
    {
      value: valueProp,
      defaultValue = 0,
      min = 0,
      max = 100,
      step = 1,
      isDisabled = false,
      onChange,
      onChangeStart,
      onChangeEnd,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      ...props
    },
    ref
  ) => {
    const [value, setValue] = useState(valueProp ?? defaultValue);
    const currentValue = valueProp ?? value;

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = parseFloat(event.target.value);
        setValue(newValue);
        onChange?.(newValue);
      },
      [onChange]
    );

    const handleMouseDown = useCallback(() => {
      onChangeStart?.(currentValue);
    }, [onChangeStart, currentValue]);

    const handleMouseUp = useCallback(() => {
      onChangeEnd?.(currentValue);
    }, [onChangeEnd, currentValue]);

    const percentage = ((currentValue - min) / (max - min)) * 100;

    return (
      <Box
        ref={ref}
        position="relative"
        width="100%"
        height={4}
        display="flex"
        alignItems="center"
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        opacity={isDisabled ? 0.6 : 1}
        {...props}
      >
        {/* Track */}
        <Box
          width="100%"
          height={1}
          backgroundColor="#E2E8F0"
          borderRadius="full"
          position="relative"
          overflow="hidden"
        >
          {/* Filled track */}
          <Box
            height="100%"
            backgroundColor="#3182CE"
            borderRadius="full"
            width={`${percentage}%`}
            transition="width 0.2s"
          />
        </Box>

        {/* Hidden input for accessibility */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={currentValue}
          onChange={handleChange}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          disabled={isDisabled}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            opacity: 0,
            cursor: 'inherit',
          }}
        />

        {/* Thumb */}
        <Box
          position="absolute"
          left={`${percentage}%`}
          transform="translateX(-50%)"
          width={4}
          height={4}
          backgroundColor="white"
          borderRadius="full"
          border="2px solid #3182CE"
          boxShadow="0 0 0 1px rgba(0,0,0,0.1)"
          transition="left 0.2s"
          _hover={{
            transform: 'translateX(-50%) scale(1.1)',
          }}
        />
      </Box>
    );
  }
);

Slider.displayName = 'Slider';

export const SliderTrack = React.forwardRef<HTMLDivElement, SliderTrackProps>((props, ref) => {
  return <Box ref={ref} {...props} />;
});

SliderTrack.displayName = 'SliderTrack';

export const SliderThumb = React.forwardRef<HTMLDivElement, SliderThumbProps>((props, ref) => {
  return <Box ref={ref} {...props} />;
});

SliderThumb.displayName = 'SliderThumb';
